import React, { Suspense, lazy, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';

import './theme/tokens.css';
import './theme/base.css';

/* The two halves of the product are loaded as separate chunks.
 *
 * This is a security boundary, not a page-weight optimisation. HR code knows
 * how to render `is_correct`, `explanation` and answer keys; a candidate's
 * browser should never receive those components at all, not even as dead code
 * behind a route guard that a devtools user could step over. Because the only
 * references to ./hr and ./exam are these dynamic imports, Rollup emits two
 * disjoint chunks and a candidate downloads only the exam one.
 *
 * Therefore: NOTHING in this file, or in anything it imports statically, may
 * import from ./hr or ./exam. A single static import collapses both chunks back
 * into the entry bundle and silently undoes this.
 */
const HrApp = lazy(() => import('./hr/HrApp'));
const ExamApp = lazy(() => import('./exam/ExamApp'));

function RouteFallback() {
  return (
    <div
      style={{
        minHeight: '60vh',
        display: 'grid',
        placeItems: 'center',
        color: 'var(--text-3)',
        fontSize: '0.875rem',
      }}
    >
      <div style={{ display: 'grid', gap: '0.875rem', justifyItems: 'center' }}>
        <span
          aria-hidden="true"
          style={{
            width: 22,
            height: 22,
            borderRadius: '50%',
            border: '2px solid var(--bg-3)',
            borderTopColor: 'var(--blue)',
            animation: 'spin .7s linear infinite',
          }}
        />
        <span role="status">Loading…</span>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
    </div>
  );
}

interface BoundaryState {
  failed: boolean;
  isChunkError: boolean;
}

/**
 * A dynamic import that 404s surfaces with wording that differs per browser, so
 * match on the shapes rather than one exact string. Anything unrecognised is
 * treated as OUR bug, which is the safer default: mislabelling a crash as a
 * network blip is how a defect stays hidden.
 */
const RELOAD_FLAG = 'osto.chunk-reload';

/** Same URL plus a throwaway parameter, so nothing can be served from cache. */
function reloadWithCacheBust(): void {
  const url = new URL(window.location.href);
  url.searchParams.set('_r', String(performance.now() | 0));
  window.location.replace(url.toString());
}

function looksLikeChunkError(error: unknown): boolean {
  const msg = error instanceof Error ? `${error.name}: ${error.message}` : String(error);
  return /dynamically imported module|Loading chunk|ChunkLoadError|Importing a module script failed|error loading dynamically imported/i.test(
    msg,
  );
}

/**
 * Catches anything a route subtree throws during render.
 *
 * It distinguishes two cases, and the distinction matters. A lazy chunk can 404
 * when the server is redeployed while a tab is open — the hashed filename the
 * running page remembers no longer exists — and reloading genuinely fixes that.
 * Any other error is a bug in our code, and reloading will almost certainly
 * reproduce it.
 *
 * Both used to render "the network dropped". That was wrong twice over: it told
 * a candidate to blame their connection for our defect, and it hid the defect
 * from us. A TypeError in the preflight screen showed up as a connection
 * problem, which is exactly the wrong place to go looking. Whatever the cause,
 * the reassurance is true and worth keeping: answers are already server-side.
 */
class ChunkBoundary extends React.Component<{ children: React.ReactNode }, BoundaryState> {
  state: BoundaryState = { failed: false, isChunkError: false };

  static getDerivedStateFromError(error: unknown): BoundaryState {
    return { failed: true, isChunkError: looksLikeChunkError(error) };
  }

  componentDidMount() {
    // A chunk failure gets ONE automatic hard reload, then stops.
    //
    // The manual Reload button is not enough on its own. /assets/ is served
    // `immutable, max-age=31536000`, so once a browser has a bad entry for a
    // hashed filename — which a redeploy can produce, since the old container
    // answers for a moment after the new index.html has been handed out — an
    // ordinary reload reads that same poisoned entry straight back out of cache
    // and fails identically. A candidate mid-exam would sit on a dead screen
    // pressing a button that cannot work.
    //
    // reloadWithCacheBust appends a one-shot query parameter, which makes the
    // browser treat every module URL on the page as new and refetch it.
    //
    // sessionStorage holds the "already tried" flag: it survives the reload and
    // dies with the tab, so a genuinely broken build gives up after one attempt
    // and shows the message instead of reloading forever.
    if (!this.state.isChunkError) return;
    try {
      if (sessionStorage.getItem(RELOAD_FLAG)) return;
      sessionStorage.setItem(RELOAD_FLAG, '1');
    } catch {
      // Private mode or blocked storage. One reload is still better than none,
      // but without the flag it could loop, so do nothing.
      return;
    }
    reloadWithCacheBust();
  }

  componentDidCatch(error: unknown) {
    // Tagged distinctly so a real crash is greppable and never reads as a blip.
    console.error(
      looksLikeChunkError(error)
        ? '[osto-hire] route chunk failed to load'
        : '[osto-hire] UNCAUGHT RENDER ERROR (this is a bug, not the network)',
      error,
    );
  }

  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <div className="shell" style={{ paddingTop: '18vh', maxWidth: 520 }}>
        <div className="card" style={{ padding: '1.75rem' }}>
          <span className="lbl">{this.state.isChunkError ? 'Connection' : 'Error'}</span>
          <h1 style={{ marginTop: '0.75rem', fontSize: '1.375rem' }}>
            This page could not finish loading
          </h1>
          <p style={{ marginTop: '0.5rem', color: 'var(--text-2)' }}>
            The application was updated or the network dropped. Reloading is safe — every
            answer you have given is already saved on the server.
          </p>
          <button
            type="button"
            className="btn btn-primary"
            style={{ marginTop: '1.25rem' }}
            onClick={reloadWithCacheBust}
          >
            Reload
          </button>
        </div>
      </div>
    );
  }
}

function App() {
  // Clearing on a successful render is what makes the guard one-shot PER
  // FAILURE rather than one-shot per tab. Without it, a tab that recovered from
  // one deploy would have no automatic recovery left for the next.
  useEffect(() => {
    try {
      sessionStorage.removeItem(RELOAD_FLAG);
    } catch {
      // Storage unavailable. Nothing to clear, nothing to do.
    }
  }, []);

  return (
    <BrowserRouter>
      <ChunkBoundary>
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/hr/*" element={<HrApp />} />
            <Route path="/exam/*" element={<ExamApp />} />
            {/* The root is the candidate door. HR reach their console by a
                bookmarked /hr link; candidates are the ones who arrive at the
                bare hostname from an invitation email. */}
            <Route path="/" element={<Navigate to="/exam/login" replace />} />
            <Route path="*" element={<Navigate to="/exam/login" replace />} />
          </Routes>
        </Suspense>
      </ChunkBoundary>
    </BrowserRouter>
  );
}

const container = document.getElementById('root');
if (!container) throw new Error('#root missing from index.html');

createRoot(container).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
